const VERSION_STORAGE_KEY = 'app_version';

/**
 * Get current app version from package.json
 */
export const getAppVersion = (): string => {
  return (process.env.REACT_APP_VERSION as string) || '1.0.0';
};

/**
 * Get stored version from localStorage
 */
const getStoredVersion = (): string | null => {
  try {
    return localStorage.getItem(VERSION_STORAGE_KEY);
  } catch (error) {
    console.error('Error reading version from localStorage:', error);
    return null;
  }
};

/**
 * Store version in localStorage
 */
const storeVersion = (version: string): void => {
  try {
    localStorage.setItem(VERSION_STORAGE_KEY, version);
  } catch (error) {
    console.error('Error storing version in localStorage:', error);
  }
};

/**
 * Clear all localStorage data
 */
const clearLocalStorage = (): void => {
  try {
    localStorage.clear();

    console.log('LocalStorage cleared due to version change');
  } catch (error) {
    console.error('Error clearing localStorage:', error);
  }
};

/**
 * Initialize version management
 * Checks if version has changed and clears localStorage if needed
 * Should be called once at app startup
 */
export const initializeVersionManager = (): void => {
  const currentVersion = getAppVersion();
  const storedVersion = getStoredVersion();

  // If no stored version, this is first run - just store current version
  if (!storedVersion) {
    storeVersion(currentVersion);
    return;
  }

  // Check if version has changed
  const versionChanged = storedVersion !== currentVersion;

  if (versionChanged) {
    console.log('App version changed:', {
      old: storedVersion,
      new: currentVersion,
    });

    // Clear localStorage
    clearLocalStorage();

    // Store new version
    storeVersion(currentVersion);

    // Store previous version so component can show notification
    try {
      localStorage.setItem(`${VERSION_STORAGE_KEY}_previous`, storedVersion);
    } catch (error) {
      console.error('Error storing previous version:', error);
    }
  }
};

/**
 * Get current version (for display purposes)
 */
export const getVersionInfo = (): string => {
  return getAppVersion();
};

/**
 * Get previous version from localStorage (if version changed)
 */
export const getPreviousVersion = (): string | null => {
  try {
    return localStorage.getItem(`${VERSION_STORAGE_KEY}_previous`);
  } catch (error) {
    console.error('Error reading previous version:', error);
    return null;
  }
};

/**
 * Clear the previous version from localStorage
 */
export const clearPreviousVersion = (): void => {
  try {
    localStorage.removeItem(`${VERSION_STORAGE_KEY}_previous`);
  } catch (error) {
    console.error('Error clearing previous version:', error);
  }
};
